/**
 * Device health-flag registry — the FLAG_REGISTRY of the settled status model.
 *
 * Flags are ADDITIVE and orthogonal to the connectivity bucket: a device shows
 * ONE bucket (online/issue/offline/unknown) plus 0..n flags. Adding a new flag
 * means adding a registry entry (+ its derivation in the web layer) — the
 * bucket enum and every UI layer stay untouched. 'issue' is simply
 * "reachable + ≥1 flag" (see `composeStatus` in device-connectivity.ts).
 *
 * This file is pure data (registry pattern shared with MDM_COMMANDS_CATALOG):
 * icons + layout live in apps/web; derivation predicates live in
 * `features/mdm-devices/utils/derive-flags.ts` because several inputs
 * (content-expired verdict, wrongTime tiers) are app-layer helpers.
 */

export const DEVICE_FLAG_IDS = {
  /** App heartbeat dead while the device axis is provably up — the one
   *  state a remote app restart fixes. */
  APP_NOT_RUNNING: 'app-not-running',
  /** Player reports no display: heartbeat status code 2 / hdmiActive false. */
  DISPLAY_DISCONNECTED: 'display-disconnected',
  /** Device clock skew (`feature.wrongTime`, set by rest-api's HMAC-Expires
   *  relax check). Escalates in copy after 24h — see derive-flags. */
  CLOCK_WRONG: 'clock-wrong',
  /** Assigned content has expired (drawer Screen-Health verdict input). */
  CONTENT_EXPIRED: 'content-expired',
} as const;

export type DeviceFlagId = (typeof DEVICE_FLAG_IDS)[keyof typeof DEVICE_FLAG_IDS];

export interface DeviceFlagDefinition {
  id: DeviceFlagId;
  /** Flat i18n key for the short label shown as row status text. */
  labelKey: string;
  /** Flat i18n key for the action-oriented tooltip. */
  tooltipKey: string;
  /** Lower = more severe; orders multi-flag icon rows and breakdowns. */
  severity: number;
  /** Inline remediation this flag offers, if any. 'restart-app' MUST be
   *  dispatched through the command catalog (`availableCommands` gate +
   *  MDM_ACTION_HANDLERS) — never a hand-rolled mutation. */
  actionId?: 'restart-app' | 'assign-content';
}

export const DEVICE_FLAG_REGISTRY: Record<DeviceFlagId, DeviceFlagDefinition> = {
  [DEVICE_FLAG_IDS.APP_NOT_RUNNING]: {
    id: DEVICE_FLAG_IDS.APP_NOT_RUNNING,
    labelKey: 'App not running',
    tooltipKey:
      "The OptiSigns app isn't running. The device is still connected — you can restart the app remotely.",
    severity: 0,
    actionId: 'restart-app',
  },
  [DEVICE_FLAG_IDS.DISPLAY_DISCONNECTED]: {
    id: DEVICE_FLAG_IDS.DISPLAY_DISCONNECTED,
    labelKey: 'Display disconnected',
    tooltipKey: "The device can't detect a display. Check the HDMI cable and TV power.",
    severity: 1,
  },
  [DEVICE_FLAG_IDS.CLOCK_WRONG]: {
    id: DEVICE_FLAG_IDS.CLOCK_WRONG,
    labelKey: 'Device time incorrect',
    tooltipKey: 'Device time is incorrect. It will impact scheduling and other features.',
    severity: 2,
  },
  [DEVICE_FLAG_IDS.CONTENT_EXPIRED]: {
    id: DEVICE_FLAG_IDS.CONTENT_EXPIRED,
    labelKey: 'Content expired',
    tooltipKey: 'The assigned content has expired. Assign new content to this screen.',
    severity: 3,
    actionId: 'assign-content',
  },
} as const;

/** Registry entries ordered by severity (most severe first). */
export const ORDERED_DEVICE_FLAGS: DeviceFlagDefinition[] = Object.values(
  DEVICE_FLAG_REGISTRY,
).sort((a, b) => a.severity - b.severity);
